import { useState } from 'react';
import { MSym, StoreoLogo } from '@storeo/ui';
import { useAuth } from '../../lib/auth';

const STOREFRONT_ROOT = import.meta.env.VITE_STOREFRONT_ROOT_DOMAIN;
const CRIAR_URL = STOREFRONT_ROOT ? `https://${STOREFRONT_ROOT}/criar` : 'http://localhost:3000/criar';

const SPARK_PATH =
  'M0 52 C30 50, 45 42, 70 38 C95 34, 120 45, 145 28 C170 12, 195 32, 225 18 C255 4, 285 14, 320 2';

/** Tela única de login do painel — redesign Lumina (docs/prototype/storeo_login_no_centro_de_comando).
 *  SPA inteira atrás do login (ADR 0019 §1). Form embutido → `useAuth().login` (password grant
 *  no Keycloak, contrato inalterado). SSO e o showcase da esquerda são decorativos por ora. */
export function LoginScreen() {
  const { login, error, status } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const busy = status === 'checking';

  return (
    <div className="lumina relative min-h-screen w-full overflow-hidden bg-background text-on-surface flex items-center justify-center p-grid-margin-mobile md:p-grid-margin-tablet lg:p-grid-margin-desktop py-space-xl">
      <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full bg-primary-container/15 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 translate-x-1/3 translate-y-1/3 w-[32rem] h-[32rem] rounded-full bg-tertiary-container/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/2 right-1/3 w-80 h-80 rounded-full bg-secondary-container/10 blur-[100px] pointer-events-none" />

      <div className="relative z-10 w-full max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 rounded-xl bg-surface-container-low/70 backdrop-blur-2xl shadow-2xl overflow-hidden">
        {/* Left — showcase (decorativo) */}
        <div className="hidden lg:flex lg:col-span-5 relative flex-col justify-between p-space-2xl bg-linear-to-b from-surface-container-high/50 via-surface-container/60 to-surface-container-lowest/90 overflow-hidden">
          <svg className="absolute -right-24 -top-20 w-96 h-96 text-primary-container/10 pointer-events-none" fill="none" viewBox="0 0 400 400">
            <circle cx="200" cy="200" r="190" stroke="currentColor" strokeDasharray="4 8" strokeWidth="1.5" />
            <circle cx="200" cy="200" r="140" stroke="currentColor" strokeWidth="1" />
            <circle cx="200" cy="200" r="90" stroke="currentColor" strokeDasharray="2 6" strokeWidth="1.5" />
            <path d="M50 200 H350 M200 50 V350" stroke="currentColor" strokeOpacity="0.4" strokeWidth="0.75" />
          </svg>

          <div className="relative z-10 flex flex-col gap-space-lg">
            <div className="inline-flex items-center gap-space-xs px-space-sm py-space-3xs rounded-full bg-surface-container-highest/80 shadow-inner w-fit">
              <span className="w-2 h-2 rounded-full bg-secondary animate-ping" />
              <span className="w-2 h-2 rounded-full bg-secondary -ml-3" />
              <span className="font-label-sm text-label-sm text-secondary uppercase tracking-widest font-bold">
                Centro de Comando
              </span>
              <span className="text-outline text-[10px]">•</span>
              <span className="font-label-sm text-label-sm text-on-surface-variant font-medium">Tempo real</span>
            </div>
            <div className="flex flex-col gap-space-xs">
              <h2 className="font-headline-lg text-headline-lg text-on-surface font-extrabold tracking-tight">
                O centro de comando da sua marca digital.
              </h2>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Monitore fluxos multi-adquirentes, orquestre links instantâneos e converta visitantes em receita
                previsível com latência submétrica.
              </p>
            </div>
          </div>

          <div className="relative z-10 my-space-xl p-space-lg rounded-xl bg-surface-container-lowest/80 shadow-xl backdrop-blur-md">
            <div className="flex items-center justify-between pb-space-sm mb-space-sm">
              <div className="flex items-center gap-space-xs">
                <MSym name="monitoring" className="text-tertiary text-headline-sm" />
                <span className="font-label-md text-label-md text-on-surface uppercase tracking-wider font-semibold">
                  Volume em Tempo Real
                </span>
              </div>
              <div className="flex items-center gap-space-3xs px-space-xs py-space-3xs rounded bg-secondary/10 text-secondary font-label-sm text-label-sm">
                <MSym name="trending_up" className="text-label-sm" />
                <span>+32.4%</span>
              </div>
            </div>
            <div className="flex flex-col gap-space-3xs mb-space-md">
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">
                Faturamento Hoje (BRL)
              </span>
              <div className="font-metric-stat text-metric-stat text-on-surface font-extrabold tracking-tight flex items-baseline gap-space-xs">
                <span className="text-tertiary font-normal text-headline-md">R$</span>
                <span>142.890,40</span>
              </div>
            </div>
            <div className="w-full h-16 relative">
              <svg className="w-full h-full overflow-visible" fill="none" preserveAspectRatio="none" viewBox="0 0 320 64">
                <defs>
                  <linearGradient id="loginChartGlow" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#4cd7f6" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#4cd7f6" stopOpacity="0.0" />
                  </linearGradient>
                </defs>
                <path d={SPARK_PATH} fill="none" stroke="#4cd7f6" strokeLinecap="round" strokeWidth="2.5" />
                <path d={`${SPARK_PATH} L320 64 L0 64 Z`} fill="url(#loginChartGlow)" />
                <circle className="animate-pulse" cx="320" cy="2" fill="#6ffbbe" r="4" />
              </svg>
            </div>
            <div className="grid grid-cols-3 gap-space-xs pt-space-md mt-space-sm">
              {[
                { k: 'Aprovação', v: '98.2%', c: 'text-secondary' },
                { k: 'Checkouts', v: '1.408', c: 'text-on-surface' },
                { k: 'Latência', v: '84ms', c: 'text-primary' },
              ].map((t) => (
                <div key={t.k} className="p-space-xs rounded-lg bg-surface-container/60 flex flex-col gap-space-3xs">
                  <span className="font-label-sm text-label-sm text-outline">{t.k}</span>
                  <span className={`font-headline-sm text-headline-sm font-bold ${t.c}`}>{t.v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="relative z-10 flex flex-col gap-space-md">
            <div className="flex items-center gap-space-md p-space-sm rounded-xl bg-surface-container-high/40">
              <span className="w-12 h-12 rounded-full shrink-0 inline-flex items-center justify-center bg-linear-to-br from-primary-container to-tertiary-container text-on-primary-container font-bold shadow-inner">
                M
              </span>
              <div className="flex flex-col min-w-0">
                <span className="font-label-lg text-label-lg text-on-surface font-semibold truncate">Mariana Castello</span>
                <span className="font-body-sm text-body-sm text-on-surface-variant truncate">
                  &ldquo;Migramos 6 operações para a Storeo. A estabilidade no checkout é incomparável.&rdquo;
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between text-on-surface-variant/70 pt-space-xs">
              <div className="flex items-center gap-space-3xs text-label-sm font-label-sm">
                <MSym name="shield" className="text-secondary text-label-md" />
                <span>PCI-DSS Nível 1</span>
              </div>
              <div className="flex items-center gap-space-3xs text-label-sm font-label-sm">
                <MSym name="lock" className="text-tertiary text-label-md" />
                <span>LGPD Compliant</span>
              </div>
              <div className="flex items-center gap-space-3xs text-label-sm font-label-sm">
                <MSym name="gpp_good" className="text-primary text-label-md" />
                <span>Antifraude nativo</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right — form */}
        <div className="col-span-1 lg:col-span-7 flex flex-col justify-center p-grid-margin-mobile md:p-space-2xl lg:p-space-3xl bg-surface-container-low/95">
          <div className="w-full max-w-xl mx-auto flex flex-col gap-space-xl">
            <div className="flex flex-col gap-space-sm">
              <div className="flex items-center gap-space-sm">
                <div className="relative flex items-center justify-center w-12 h-12 rounded-xl bg-surface-container-high shadow-[0_0_24px_rgba(99,102,241,0.35)] p-space-2xs">
                  <StoreoLogo variant="mark" className="w-full h-full" />
                </div>
                <div className="flex items-baseline gap-space-3xs">
                  <span className="font-headline-md text-headline-md text-on-surface font-bold tracking-tighter">storeo</span>
                  <span className="w-2 h-2 rounded-full bg-primary-container inline-block" />
                </div>
              </div>
              <div className="flex flex-col gap-space-3xs pt-space-xs">
                <h1 className="font-headline-lg text-headline-lg text-on-surface font-extrabold tracking-tight">
                  Acesse seu centro de comando
                </h1>
                <p className="font-body-md text-body-md text-on-surface-variant">
                  Entre para gerenciar suas lojas, métricas de conversão e rotas de pagamento.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-space-sm">
              <button
                type="button"
                disabled
                title="Em breve"
                className="flex items-center justify-center gap-space-sm px-space-md py-space-sm rounded-lg bg-surface-container text-on-surface shadow-sm opacity-60 cursor-not-allowed"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.25-.94 2.31-2 3.02v2.51h3.24c1.89-1.74 2.98-4.3 2.98-7.39 0-.71-.06-1.4-.32-2.12z" />
                  <path d="M12.18 22c2.7 0 4.97-.89 6.63-2.42l-3.24-2.51c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.21v2.59C4.87 19.74 8.27 22 12.18 22z" />
                  <path d="M6.57 13.9c-.2-.6-.31-1.24-.31-1.9s.11-1.3.31-1.9V7.51H3.21C2.56 8.8 2.18 10.26 2.18 12s.38 3.2 1.03 4.49l3.36-2.59z" />
                  <path d="M12.18 5.97c1.47 0 2.79.51 3.83 1.5l2.87-2.87C17.15 3.03 14.88 2 12.18 2 8.27 2 4.87 4.26 3.21 7.51l3.36 2.59c.79-2.37 3-4.13 5.61-4.13z" />
                </svg>
                <span className="font-label-lg text-label-lg font-semibold">Google Workspace</span>
              </button>
              <button
                type="button"
                disabled
                title="Em breve"
                className="flex items-center justify-center gap-space-sm px-space-md py-space-sm rounded-lg bg-surface-container text-on-surface shadow-sm opacity-60 cursor-not-allowed"
              >
                <svg className="w-5 h-5 fill-current" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.65-.79 1.1-1.9.98-3.01-.95.04-2.11.64-2.79 1.43-.6.69-1.12 1.82-.98 2.9 1.06.08 2.14-.53 2.79-1.32z" />
                </svg>
                <span className="font-label-lg text-label-lg font-semibold">Continuar com Apple</span>
              </button>
            </div>

            <div className="relative flex items-center justify-center">
              <div className="w-full h-px bg-surface-container-highest" />
              <span className="absolute px-space-sm bg-surface-container-low font-label-sm text-label-sm text-outline uppercase tracking-wider">
                ou com seu e-mail corporativo
              </span>
            </div>

            <form
              className="flex flex-col gap-space-lg"
              onSubmit={(e) => {
                e.preventDefault();
                login(email, password);
              }}
            >
              <div className="flex flex-col gap-space-2xs">
                <label htmlFor="email" className="font-label-md text-label-md text-on-surface flex items-center justify-between">
                  <span>E-mail da Loja</span>
                  <span className="text-secondary font-label-sm text-label-sm flex items-center gap-space-3xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
                    Conexão Criptografada
                  </span>
                </label>
                <div className="relative flex items-center">
                  <MSym name="alternate_email" className="absolute left-space-sm text-outline text-headline-sm pointer-events-none" />
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="username"
                    placeholder="fundador@sualoja.com.br"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 pl-12 pr-space-md rounded-lg bg-surface-container-lowest text-on-surface placeholder:text-outline/60 font-body-md text-body-md focus:outline-none focus:bg-surface-container transition-all shadow-inner"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-space-2xs">
                <div className="flex items-center justify-between">
                  <label htmlFor="password" className="font-label-md text-label-md text-on-surface">
                    Senha
                  </label>
                  <a className="font-label-sm text-label-sm text-primary hover:text-primary-fixed transition-colors" href="#">
                    Recuperar acesso?
                  </a>
                </div>
                <div className="relative flex items-center">
                  <MSym name="lock" className="absolute left-space-sm text-outline text-headline-sm pointer-events-none" />
                  <input
                    id="password"
                    type={showPw ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full h-12 pl-12 pr-12 rounded-lg bg-surface-container-lowest text-on-surface placeholder:text-outline/60 font-body-md text-body-md focus:outline-none focus:bg-surface-container transition-all shadow-inner tracking-widest"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label="Mostrar ou ocultar a senha"
                    className="absolute right-space-sm text-outline hover:text-on-surface transition-colors focus:outline-none p-space-3xs"
                  >
                    <MSym name={showPw ? 'visibility_off' : 'visibility'} className="text-headline-sm" />
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-space-xs pt-space-3xs">
                <label className="flex items-center gap-space-xs cursor-pointer select-none group">
                  <input
                    type="checkbox"
                    defaultChecked
                    className="w-4 h-4 rounded bg-surface-container-high accent-primary cursor-pointer"
                  />
                  <span className="font-body-sm text-body-sm text-on-surface-variant group-hover:text-on-surface transition-colors">
                    Lembrar deste terminal por 30 dias
                  </span>
                </label>
                <div className="flex items-center gap-space-3xs font-label-sm text-label-sm text-outline">
                  <MSym name="fingerprint" className="text-label-md text-tertiary" />
                  <span>Biometria habilitada</span>
                </div>
              </div>

              {error ? (
                <p className="rounded-lg bg-error-container/25 text-on-error-container px-space-md py-space-sm font-body-sm text-body-sm">
                  {error}
                </p>
              ) : null}

              <button
                type="submit"
                disabled={busy}
                className="relative w-full h-12 rounded-lg bg-linear-to-r from-primary-container via-primary-container to-tertiary-container text-on-primary-container font-headline-sm text-headline-sm font-bold shadow-[0_0_24px_rgba(99,102,241,0.35)] hover:shadow-[0_0_36px_rgba(99,102,241,0.55)] transition-all flex items-center justify-center gap-space-sm overflow-hidden group disabled:opacity-70"
              >
                {busy ? (
                  <>
                    <MSym name="progress_activity" className="relative z-10 text-headline-sm animate-spin" />
                    <span className="relative z-10 font-semibold tracking-wide">Autenticando sessão segura…</span>
                  </>
                ) : (
                  <>
                    <span className="relative z-10 font-semibold tracking-wide">Entrar no Centro de Comando</span>
                    <MSym name="arrow_forward" className="relative z-10 text-headline-sm group-hover:translate-x-1 transition-transform" />
                  </>
                )}
                <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            </form>

            <div className="flex flex-col sm:flex-row items-center justify-between p-space-md rounded-xl bg-surface-container gap-space-sm shadow-md">
              <div className="flex items-center gap-space-sm">
                <div className="w-8 h-8 rounded-lg bg-secondary-container/20 text-secondary flex items-center justify-center">
                  <MSym name="storefront" className="text-headline-sm" />
                </div>
                <div className="flex flex-col">
                  <span className="font-label-md text-label-md text-on-surface font-bold">Novo na plataforma Storeo?</span>
                  <span className="font-body-sm text-body-sm text-on-surface-variant">
                    Comece a vender com a menor taxa do mercado
                  </span>
                </div>
              </div>
              <a
                href={CRIAR_URL}
                className="px-space-md py-space-xs rounded-lg bg-surface-container-high text-primary hover:bg-primary hover:text-on-primary font-label-md text-label-md font-semibold transition-all whitespace-nowrap shadow-sm"
              >
                Criar Loja
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-between text-outline text-label-sm font-label-sm pt-space-xs">
              <div className="flex items-center gap-space-3xs">
                <MSym name="headset_mic" className="text-label-md" />
                <span>Dúvidas com sua conta?</span>
                <a className="text-tertiary hover:underline font-semibold ml-space-3xs" href="#">
                  Suporte 24/7
                </a>
              </div>
              <div className="flex items-center gap-space-md">
                <a className="hover:text-on-surface transition-colors flex items-center gap-space-3xs" href="#">
                  <MSym name="send" className="text-label-md" />
                  <span>Comunidade Telegram</span>
                </a>
                <span className="text-surface-container-highest">|</span>
                <a className="hover:text-on-surface transition-colors" href="#">
                  API Docs
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
